import { useState } from "react";

const industries = [
  "Elektryk",
  "Hydraulik",
  "Dekarz",
  "Brukarz",
  "Ogrodzenia",
  "Carporty",
  "Klimatyzacja",
  "Firma remontowa",
  "Malarz",
  "Firma budowlana",
  "Inne",
];

const cities = [
  "Warszawa",
  "Kraków",
  "Wrocław",
  "Poznań",
  "Gdańsk",
  "Katowice",
  "Łódź",
  "Lublin",
  "Inne miasto",
];

type Budget = "start" | "firma" | "pro" | "";

const budgetLabels: Record<string, string> = {
  start: "Start (wizytówka)",
  firma: "Firma (standard)",
  pro: "Pro (rozbudowana)",
};

const getEstimate = (industry: string, city: string, budget: Budget): { min: number; max: number } => {
  const bigCities = ["Warszawa", "Kraków", "Wrocław", "Poznań", "Gdańsk"];
  const cityMult = bigCities.includes(city) ? 1.4 : 1;
  const budgetMult = budget === "pro" ? 1.5 : budget === "firma" ? 1.2 : 1;
  const base = industry ? 12 : 10;
  const min = Math.round(base * cityMult * budgetMult);
  const max = Math.round((base + 14) * cityMult * budgetMult);
  return { min, max };
};

const CalculatorSection = () => {
  const [industry, setIndustry] = useState("");
  const [city, setCity] = useState("");
  const [budget, setBudget] = useState<Budget>("");
  const [result, setResult] = useState<{ min: number; max: number } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = () => {
    if (!industry || !city || !budget) return;
    setLoading(true);
    setTimeout(() => {
      setResult(getEstimate(industry, city, budget));
      setLoading(false);
    }, 1200);
  };

  const handleScrollToContact = () => {
    const el = document.querySelector("#kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const isReady = industry && city && budget;

  return (
    <section id="kalkulator" className="py-20 lg:py-28 bg-gradient-to-br from-[#0F172A] via-[#1e3a8a] to-[#1D4ED8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block text-[#93C5FD] text-sm font-semibold tracking-wide uppercase mb-4">
            Kalkulator zapytań
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-5 leading-tight">
            Sprawdź ile możesz mieć zapytań
          </h2>
          <p className="text-[#94A3B8] text-lg leading-relaxed">
            Podaj branżę, miasto i zakres — zobaczysz symulację możliwych zapytań po wdrożeniu strony.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 lg:p-8">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {/* Industry */}
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Branża</label>
                <select
                  value={industry}
                  onChange={(e) => { setIndustry(e.target.value); setResult(null); }}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#60A5FA] cursor-pointer appearance-none"
                >
                  <option value="" className="bg-[#1e3a8a] text-white">Wybierz branżę</option>
                  {industries.map(i => (
                    <option key={i} value={i} className="bg-[#1e3a8a] text-white">{i}</option>
                  ))}
                </select>
              </div>
              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Miasto</label>
                <select
                  value={city}
                  onChange={(e) => { setCity(e.target.value); setResult(null); }}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#60A5FA] cursor-pointer appearance-none"
                >
                  <option value="" className="bg-[#1e3a8a] text-white">Wybierz miasto</option>
                  {cities.map(c => (
                    <option key={c} value={c} className="bg-[#1e3a8a] text-white">{c}</option>
                  ))}
                </select>
              </div>
              {/* Budget */}
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Zakres strony</label>
                <select
                  value={budget}
                  onChange={(e) => { setBudget(e.target.value as Budget); setResult(null); }}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#60A5FA] cursor-pointer appearance-none"
                >
                  <option value="" className="bg-[#1e3a8a] text-white">Wybierz zakres</option>
                  {Object.entries(budgetLabels).map(([k, v]) => (
                    <option key={k} value={k} className="bg-[#1e3a8a] text-white">{v}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleCalculate}
              disabled={!isReady || loading}
              className="whitespace-nowrap w-full py-4 rounded-xl font-bold text-base bg-[#1D4ED8] text-white hover:bg-[#2563EB] transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Analizuję…
                </span>
              ) : "Sprawdź szacunkowe zapytania"}
            </button>

            {/* Result */}
            {result && (
              <div className="mt-6 bg-white rounded-2xl p-6 text-center">
                <p className="text-[#64748B] text-sm mb-2">
                  Szacunkowe zapytania miesięcznie dla{" "}
                  <strong className="text-[#0F172A]">{industry}</strong> w{" "}
                  <strong className="text-[#0F172A]">{city}</strong>:
                </p>
                <div className="flex items-center justify-center gap-3 my-4">
                  <span className="text-5xl lg:text-6xl font-extrabold text-[#1D4ED8]">
                    {result.min}–{result.max}
                  </span>
                  <div className="text-left">
                    <p className="text-sm font-bold text-[#0F172A]">zapytań</p>
                    <p className="text-xs text-[#64748B]">miesięcznie</p>
                  </div>
                </div>
                <p className="text-xs text-[#94A3B8] mb-5">
                  * To symulacja na podstawie danych rynkowych. Wyniki zależą od jakości strony i konkurencji w Twojej lokalizacji.
                </p>
                <button
                  onClick={handleScrollToContact}
                  className="whitespace-nowrap px-8 py-3.5 rounded-xl font-bold bg-[#1D4ED8] text-white hover:bg-[#1e40af] transition-all cursor-pointer text-base"
                >
                  Chcę taką stronę
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;
